// API Filter Parameter Interfaces

export interface OfferingFilters {
  type?: string;
  vendorId?: number;
  clientId?: number;
  categoryIds?: number[];
  priceMin?: number;
  priceMax?: number;
  status?: string;
}

export interface PackageFilters {
  mode?: string;
  duration?: number;
  capacityMin?: number;
  capacityMax?: number;
  priceMin?: number;
  priceMax?: number;
}

export interface ResourceFilters {
  vendorId?: number;
  clientId?: number;
  capacityMin?: number;
  capacityMax?: number;
  slotDurations?: number[];
}

export interface BenefitFilters {
  clientId?: number;
  vendorId?: number;
  discountType?: string;
  expiryFrom?: string;
  expiryTo?: string;
}

// Legacy API Response Types (keeping for compatibility)
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  total: number;
  page: number;
  pageSize: number;
}

// API Response Wrapper Types based on cms-endpoints.md

// Base response interface
interface BaseApiResponse {
  success: boolean;
  message: string | null;
  timestamp: string;
  traceId: string;
}

// Success response for single items
export interface ApiSuccessResponse<T> extends BaseApiResponse {
  success: true;
  data: T;
  message: string;
}

// Success response for paginated items
export interface ApiPagedResponse<T> extends BaseApiResponse {
  success: true;
  data: T[];
  message: string;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// Error response
export interface ErrorResponse extends BaseApiResponse {
  success: false;
  data: null;
  message: null;
  error: {
    code: string;
    message: string;
    details?: string | null;
  };
}

// Unified wrapper type for all possible API responses
export type ApiResponseWrapper<T> = ApiSuccessResponse<T> | ApiPagedResponse<T> | ErrorResponse;

// Type guards to check response type
export function isApiSuccessResponse<T>(response: ApiResponseWrapper<T>): response is ApiSuccessResponse<T> {
  return response.success === true && !('pagination' in response);
}

export function isApiPagedResponse<T>(response: ApiResponseWrapper<T>): response is ApiPagedResponse<T> {
  return response.success === true && 'pagination' in response;
}

export function isErrorResponse<T>(response: ApiResponseWrapper<T>): response is ErrorResponse {
  return response.success === false;
}

// Utility functions to extract data safely
export function extractApiData<T>(response: ApiResponseWrapper<T>): T | null {
  if (isApiSuccessResponse(response)) {
    return response.data;
  }
  return null;
}

export function extractPagedApiData<T>(response: ApiResponseWrapper<T>): { data: T[]; pagination: ApiPagedResponse<T>['pagination'] } | null {
  if (isApiPagedResponse(response)) {
    return { data: response.data, pagination: response.pagination };
  }
  return null;
}

export function extractApiError(response: ApiResponseWrapper<any>): { code: string; message: string; details?: string | null } | null {
  if (isErrorResponse(response)) {
    return response.error;
  }
  return null;
}

// Query Parameter Builders
export function buildQueryString(params: Record<string, any>): string {
  const queryParts: string[] = [];
  
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    
    if (Array.isArray(value)) {
      value.forEach(item => {
        queryParts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`);
      });
    } else {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  
  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}